<script lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import Button1XP from '../Button1XP/Button1XP.vue';
import Icon from '../Icon/Icon.vue';

import type { MessageType, SnackbarButton, SnackbarPattern } from '../../types/snackbar.ts';

type Props = {
  type?: MessageType;
  message?: string;
  buttons?: SnackbarButton[];
  open: boolean;
  pattern?: SnackbarPattern;
  duration?: number;
};
</script>

<script setup lang="ts">
const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  pattern: 'toast',
  duration: 5000,
});

const emit = defineEmits<{
  buttonClicked: [button: SnackbarButton];
  close: [];
  'update:open': [value: boolean];
}>();

const isOpen = ref<boolean>(props.open);

let closeTimeout: ReturnType<typeof setTimeout> | undefined;

const icon = (type: MessageType) => {
  if (type === 'warning') {
    return 'warning';
  } else if (type === 'error') {
    return 'error';
  } else if (type === 'success') {
    return 'check_circle';
  } else {
    return 'info';
  }
};

const clearCloseTimeout = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = undefined;
  }
};

const close = () => {
  clearCloseTimeout();

  isOpen.value = false;

  emit('update:open', false);
  emit('close');
};

const startCloseTimeout = () => {
  clearCloseTimeout();

  if (props.pattern !== 'toast' || !isOpen.value) {
    return;
  }

  closeTimeout = setTimeout(() => {
    close();
  }, props.duration);
};

const handleButtonClick = (button: SnackbarButton) => {
  close();
  emit('buttonClicked', button);
};

watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue;

    if (newValue && !props.buttons) {
      startCloseTimeout();
    } else {
      clearCloseTimeout();
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => props.pattern,
  () => {
    if (isOpen.value && !props.buttons) {
      startCloseTimeout();
    }
  },
);

onBeforeUnmount(() => {
  clearCloseTimeout();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      aria-atomic="true"
      :class="[
        $style.container,
        $style[pattern],
        type === 'info' && $style.info,
        type === 'warning' && $style.warning,
        type === 'error' && $style.error,
        type === 'success' && $style.success,
      ]"
      role="alert"
    >
      <div :class="$style.content">
        <Icon aria-hidden="true" :class="$style.icon" :name="icon(type)" />

        <p v-if="message" :class="$style.message">
          {{ message }}
        </p>

        <p v-else :class="$style.message">
          <slot />
        </p>
      </div>

      <div :class="$style.actions">
        <Button1XP
          v-for="(button, index) in buttons"
          :key="index"
          :aria-label="button.ariaLabel"
          :button-type="button.buttonType"
          :end-icon="button.endIcon"
          :size="button.size"
          :start-icon="button.startIcon"
          :target="button.target"
          :text="button.text"
          :url="button.url"
          :variant="button.variant"
          @click="handleButtonClick(button)"
        />

        <button
          v-if="pattern === 'persistent' && !buttons?.length"
          aria-label="Meldung schließen"
          :class="$style.close"
          type="button"
          @click="close"
        >
          <Icon aria-hidden="true" :class="$style.closeIcon" name="close" />
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" module src="./Snackbar.scss" />
